
export const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getCurrentDateLocal = (): string => {
  return formatDateToLocal(new Date());
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return formatDateToLocal(date1) === formatDateToLocal(date2);
};


export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const parseLocalCalendarDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const datePart = dateString.trim().split('T')[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }
  return parseDate(dateString);
};

/**
 * Format date in short format (e.g., "Jan 15")
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export const formatDateShort = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date with year (e.g., "Mar 13, 2025")
 * @param date - Date string or Date object
 * @returns Formatted date string with year
 */
export const formatDateWithYear = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Time only, en-US 12-hour with AM/PM (e.g. "2:30 PM") — same convention as flight result cards.
 */
export const formatTimeToAmPm = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Calculate number of days between two dates (inclusive)
 * For same day trips, returns 1 day minimum
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @returns Number of days between dates (minimum 1)
 */
export const calculateDaysBetween = (startDate: string | Date, endDate: string | Date): number => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const startOfDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const diffTime = endOfDay.getTime() - startOfDay.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(1, diffDays + 1);
};

/**
 * Format trip duration as "X day" or "X days"
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @returns Formatted duration string
 */
export const formatTripDuration = (startDate: string | Date, endDate: string | Date): string => {
  const days = calculateDaysBetween(startDate, endDate);
  return days === 1 ? '1 day' : `${days} days`;
};

/**
 * Format date range with year (e.g., "Jan 15 - Feb 20, 2024")
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @returns Formatted date range string
 */
export const formatDateRangeWithYear = (startDate: string | Date, endDate: string | Date): string => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const startFormatted = formatDateShort(start);
  const endFormatted = formatDateShort(endDate);
  const year = start.getFullYear();

  return `${startFormatted} - ${endFormatted}, ${year}`;
};

/**
 * Format date with weekday (e.g., "Wednesday, Jul 9")
 * @param date - Date string or Date object
 * @returns Formatted date string with weekday
 */
export const formatDateWithWeekday = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date range with weekdays (e.g., "Wednesday, Jul 9 - Friday, Jul 25")
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @returns Formatted date range string with weekdays
 */
export const formatDateRangeWithWeekdays = (startDate: string | Date, endDate: string | Date): string => {
  const startFormatted = formatDateWithWeekday(startDate);
  const endFormatted = formatDateWithWeekday(endDate);

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Format date range with year and duration (e.g., "Mar 6 - Mar 8, 2025 • 3 days")
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @returns Formatted date range string with duration
 */
export const formatDateRangeWithDuration = (startDate: string | Date, endDate: string | Date): string => {
  const dateRange = formatDateRangeWithYear(startDate, endDate);
  const duration = formatTripDuration(startDate, endDate);

  return `${dateRange} • ${duration}`;
};

/**
 * Helper function to combine date and time into ISO string
 * Used for creating activities and events
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:MM format
 * @returns ISO datetime string or empty string if invalid
 */
export const combineDateAndTime = (date: string, time: string): string => {
  if (!date || !time) return '';

  const datetime = new Date(`${date}T${time}`);
  return datetime.toISOString();
};

/**
 * Check if trip is currently active (started but not finished)
 * @param startDate - Trip start date string
 * @param endDate - Trip end date string
 * @returns True if trip is active
 */
export const isTripActive = (startDate: string, endDate: string): boolean => {
  const currentDateString = getCurrentDateLocal();
  const start = parseLocalCalendarDate(startDate);
  const end = parseLocalCalendarDate(endDate);
  if (!start || !end) return false;

  const tripStartDate = formatDateToLocal(start);
  const tripEndDate = formatDateToLocal(end);

  return tripStartDate <= currentDateString && tripEndDate >= currentDateString;
};

/**
 * Check if trip is scheduled (not yet finished)
 * @param endDate - Trip end date string
 * @returns True if trip is scheduled
 */
export const isTripScheduled = (endDate: string): boolean => {
  const currentDateString = getCurrentDateLocal();
  const end = parseLocalCalendarDate(endDate);
  if (!end) return false;

  const tripEndDate = formatDateToLocal(end);

  return tripEndDate >= currentDateString;
};

/**
 * Check if trip is archived (already finished)
 * @param endDate - Trip end date string
 * @returns True if trip is archived
 */
export const isTripArchived = (endDate: string): boolean => {
  return !isTripScheduled(endDate);
};

export const parseHHmmToMinutes = (s: string): number | null => {
  const t = (s || '').trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

export type DateTimeRangeFieldErrors = {
  endDate?: string;
  endTime?: string;
};

/**
 * Validates that end date/time is strictly after start when both are provided.
 * When dates differ, only the calendar date is compared; same-day pairs also require end time > start time.
 */
export const validateEndDateTimeAfterStart = (
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string,
  options?: { endDateMessage?: string; endTimeMessage?: string }
): DateTimeRangeFieldErrors => {
  const errors: DateTimeRangeFieldErrors = {};
  const endDateMessage = options?.endDateMessage ?? 'End date cannot be before start date';
  const endTimeMessage = options?.endTimeMessage ?? 'End time must be after start time';

  if (!startDate || !endDate) return errors;

  if (endDate < startDate) {
    errors.endDate = endDateMessage;
    return errors;
  }

  if (endDate > startDate) return errors;

  if (!startTime || !endTime) return errors;

  const startMinutes = parseHHmmToMinutes(startTime);
  const endMinutes = parseHHmmToMinutes(endTime);
  if (startMinutes === null || endMinutes === null) return errors;

  if (endMinutes <= startMinutes) {
    errors.endTime = endTimeMessage;
  }

  return errors;
};

const TIME_PICKER_INTERVAL_MINUTES = 5;

const minutesToHHmm = (totalMinutes: number): string => {
  const clamped = Math.max(0, Math.min(totalMinutes, 23 * 60 + 59));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** Min selectable end time (HH:mm) when start and end share the same calendar day. */
export const getMinEndTimeForSameDay = (
  startDate?: string,
  endDate?: string,
  startTime?: string
): string | undefined => {
  if (!startDate || !endDate || startDate !== endDate || !startTime) return undefined;

  const startMinutes = parseHHmmToMinutes(startTime);
  if (startMinutes === null) return undefined;

  const nextSlot = startMinutes + TIME_PICKER_INTERVAL_MINUTES;
  if (nextSlot > 23 * 60 + 59) return '23:59';

  return minutesToHHmm(nextSlot);
};

export const getTimeMinutesFromIso = (iso: string): number => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return d.getHours() * 60 + d.getMinutes();
};
